import { HowTo } from '../types';

/**
 * Dashboard How Tos.
 *
 * The page everyone lands on after their PIN, and — more to the point — where
 * attendance actually gets recorded day to day. The Attendance page guides
 * cover the week grid, which is where records are corrected; these cover the
 * cards, which is where they are made. Both are cross-linked, because people
 * search from wherever they happen to be standing.
 */
export const dashboardHowTos: HowTo[] = [
  {
    key: 'dashboard.sign-in-out',
    version: 1,
    section: 'Dashboard',
    title: 'Sign a child in and out',
    summary:
      'Record a child arriving and leaving from the dashboard — and say why when the time is not the scheduled one.',
    youNeed: ['A session for the child today'],
    keywords: [
      'sign in', 'sign out', 'signin', 'signout', 'arrive', 'arrived',
      'arrival', 'leave', 'left', 'departure', 'depart', 'collect', 'collected',
      'pick up', 'attendance', 'register', 'record', 'present', 'here',
      'early', 'late', 'on time', 'on-time', 'comment', 'forgot', 'forgotten',
      'retrospective', 'retrospectively', 'afterwards', 'after the fact',
      'catch up', 'backdate', 'wrong time', 'no-show', 'no show', 'absent',
      'absence', 'did not attend', 'didn\'t come', 'missing', 'today',
    ],
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Dashboard** from the menu.',
        target: 'nav.dashboard',
        route: '/',
        navigateOnly: true,
      },
      {
        text: 'The **NOW** area holds sessions running at this moment, the **UP NEXT** area anything starting soon. Both carry the same buttons, so an early arrival is signed in from **UP NEXT**.',
      },
      {
        text: 'Click **Sign in** on the child\'s card. The button reads **Sign in**, **Sign in (Early)** or **Sign in (Late)** — the app picks which from the clock, and that word goes into the record.',
        target: 'dash.sign-in-out',
      },
      {
        text: 'An on-time sign-in records straight away. Early or late, a popup opens first with the **time** it will record, which you can change, and an optional **Comment**.',
      },
      {
        text: 'A late sign-in adds **Actually was**: **On time** or **Late**. Choose **On time** when the child was punctual and only the recording was late — the time snaps back to the scheduled one.',
      },
      {
        text: 'An early sign-in has no such choice: the scheduled time has not arrived yet.',
      },
      {
        text: 'When the child leaves, the button becomes **Sign out**, read the same way against the session\'s end time.',
        target: 'dash.sign-in-out',
      },
      {
        text: 'If the child never arrives, click **No-show** and choose a reason. Leaving the session untouched instead reads as nobody having got to it yet.',
        target: 'dash.no-show',
      },
      {
        text: 'Missed a day? The **NEEDS ATTENTION** area at the top lists sessions nobody finished, and you can record them from the row itself.',
        target: 'dash.attention',
      },
      {
        text: 'Each row offers **Attended** at the scheduled times, **No-show** with a reason, or **Cancel session** if it never ran — a closure or a trip. The **…** button sets the real arrival and departure times instead.',
      },
      {
        text: 'Got one wrong? Not from here. Open the session on the **Attendance** page: change the times or comment and **Save changes**, or **Delete attendance** and start again.',
        seeAlso: { guideKey: 'attendance.remove-record', label: 'Show me the Attendance page' },
      },
    ],
    related: ['dashboard.needs-attention', 'attendance.record-today'],
  },
  {
    key: 'dashboard.needs-attention',
    version: 1,
    section: 'Dashboard',
    title: 'What the dashboard is telling you',
    summary:
      'What Needs attention is actually listing, what Dismiss all does and does not do, and why the page can look emptier than the day was.',
    keywords: [
      'dashboard', 'home', 'needs attention', 'attention', 'reminder',
      'reminders', 'dismiss', 'clear', 'missing', 'gap', 'gaps', 'unrecorded',
      'old', 'yesterday', 'last week', 'why', 'empty', 'nothing', 'just me',
      'timeline', 'strip', 'day', 'today', 'overview', 'gone', 'disappeared',
      'vanished', 'where is', 'now line',
      'filter', 'mine', 'my sessions', 'now', 'up next',
    ],
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Dashboard** from the menu.',
        target: 'nav.dashboard',
        route: '/',
        navigateOnly: true,
      },
      {
        text: 'The strip under the heading is the whole of today, laid out by time, with an amber line marking now.',
        target: 'dash.timeline',
      },
      {
        text: 'It is the only part of the page that is not a to-do list: a session already dealt with drops off the cards below but stays on the strip. Read-only — sign children in from their card.',
        target: 'dash.timeline',
      },
      {
        text: 'Below it, the **NOW** and **UP NEXT** areas are today. The **NEEDS ATTENTION** area is not: it reaches back days, listing sessions whose attendance was never finished.',
        target: 'dash.attention',
      },
      {
        text: 'A row there is usually not new — it is an earlier day. Record it from the row itself, and it clears: **Attended**, **No-show**, or **Cancel session**.',
        seeAlso: { guideKey: 'dashboard.sign-in-out', label: 'Show me how to record one' },
      },
      {
        text: '**Dismiss all** hides those rows and records **nothing**. The attendance stays missing, and the gaps are still on the Attendance page.',
        target: 'dash.dismiss-all',
      },
      {
        text: 'Dismissing clears only your own tray, and a gap appearing afterwards comes back as a new row.',
      },
      {
        text: 'If the page looks quieter than the day felt, check **Just me**. It shows only sessions you are assigned to, and stays on between visits.',
        target: 'dash.just-me',
      },
    ],
    related: ['dashboard.sign-in-out', 'attendance.record-today'],
  },
  {
    key: 'dashboard.unscheduled-session',
    version: 1,
    section: 'Dashboard',
    title: 'Add a session that was never scheduled',
    summary:
      'A child turns up who was not on the schedule — give them a session for today so their attendance and posts have something to attach to.',
    youNeed: ['The child to have a profile already'],
    keywords: [
      'unscheduled', 'not scheduled', 'unexpected', 'turned up', 'extra',
      'additional', 'add', 'new', 'session', 'today', 'one off', 'one-off',
      'cover', 'swap', 'stayed', 'extra day', 'missing session', 'no session',
    ],
    routes: ['/'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Dashboard** from the menu.',
        target: 'nav.dashboard',
        route: '/',
        navigateOnly: true,
      },
      {
        text: 'Click **Add unscheduled** at the top of the page.',
        target: 'dash.add-unscheduled',
      },
      {
        text: 'Choose the child, then check the **Start** and **End** times. They open at about now, a session\'s length apart.',
        target: 'dash.unscheduled-child',
      },
      {
        text: 'It fills in two things for you: the session is for today only, so the schedule is untouched, and you are recorded as the staff member.',
      },
      {
        text: 'Click **Add session** and it appears in the **NOW** or **UP NEXT** area, behaving like any other session: sign the child in, write posts, and it counts in attendance.',
        target: 'dash.unscheduled-add',
        seeAlso: { guideKey: 'dashboard.sign-in-out', label: 'Show me how to sign them in' },
      },
      {
        text: 'For something that repeats every week, add it on the **Weekly Schedule** instead.',
        seeAlso: { guideKey: 'schedule.add-session', label: 'Show me how to add a weekly session' },
      },
    ],
    related: ['schedule.add-session', 'dashboard.sign-in-out'],
  },
];
